import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { adminAPI } from '../api';

const EMPTY_FORM = {
  code:              '',
  label:             '',
  bonus_percent:     '',
  min_deposit:       '',
  expiry_date:       '',
  max_uses:          '',
  deduct_after_days: '30',
  is_active:         true,
};

function FormModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm]   = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    if (!form.code.trim()) return toast.error('Code is required.');
    if (!form.bonus_percent || parseFloat(form.bonus_percent) <= 0)
      return toast.error('Bonus percent must be greater than 0.');
    if (!form.deduct_after_days || parseInt(form.deduct_after_days) < 1)
      return toast.error('Deduct after days must be at least 1.');

    setSaving(true);
    try {
      const payload = {
        code:              form.code.trim().toUpperCase(),
        label:             form.label.trim() || null,
        bonus_percent:     parseFloat(form.bonus_percent),
        min_deposit:       parseFloat(form.min_deposit || 0),
        expiry_date:       form.expiry_date || null,
        max_uses:          form.max_uses ? parseInt(form.max_uses) : null,
        deduct_after_days: parseInt(form.deduct_after_days),
        is_active:         form.is_active ? 1 : 0,
      };

      if (isEdit) {
        await adminAPI.updatePromoCode(form.id, payload);
        toast.success('Promo code updated.');
      } else {
        await adminAPI.createPromoCode(payload);
        toast.success('Promo code created.');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: '1.5px solid var(--gray-200)', fontSize: 13, boxSizing: 'border-box',
    fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4, display: 'block' };
  const rowStyle   = { marginBottom: 14 };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div
        style={{
          background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 500,
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>
          {isEdit ? 'Edit Promo Code' : 'Create Promo Code'}
        </div>

        <div style={rowStyle}>
          <label style={labelStyle}>Code *</label>
          <input
            style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}
            placeholder="e.g. WELCOME50"
            value={form.code}
            onChange={e => set('code', e.target.value.toUpperCase())}
          />
          <small style={{ color: 'var(--gray-400)', fontSize: 11 }}>
            This is what users type in the recharge page.
          </small>
        </div>

        <div style={rowStyle}>
          <label style={labelStyle}>Label / Description (optional)</label>
          <input
            style={inputStyle}
            placeholder="e.g. New Year Promotion"
            value={form.label}
            onChange={e => set('label', e.target.value)}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Bonus Percentage *</label>
            <input
              style={inputStyle}
              type="number"
              min="0.01"
              step="0.01"
              placeholder="e.g. 20"
              value={form.bonus_percent}
              onChange={e => set('bonus_percent', e.target.value)}
            />
            <small style={{ color: 'var(--gray-400)', fontSize: 11 }}>
              20 means 20% added on deposit
            </small>
          </div>
          <div>
            <label style={labelStyle}>Min. Deposit (GHS)</label>
            <input
              style={inputStyle}
              type="number"
              min="0"
              step="0.01"
              placeholder="0 = any amount"
              value={form.min_deposit}
              onChange={e => set('min_deposit', e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Expiry Date (optional)</label>
            <input
              style={inputStyle}
              type="date"
              value={form.expiry_date}
              onChange={e => set('expiry_date', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>Max Uses (optional)</label>
            <input
              style={inputStyle}
              type="number"
              min="1"
              step="1"
              placeholder="Leave blank = unlimited"
              value={form.max_uses}
              onChange={e => set('max_uses', e.target.value)}
            />
          </div>
        </div>

        <div style={rowStyle}>
          <label style={labelStyle}>Deduct Bonus After (days) *</label>
          <input
            style={inputStyle}
            type="number"
            min="1"
            step="1"
            placeholder="e.g. 30"
            value={form.deduct_after_days}
            onChange={e => set('deduct_after_days', e.target.value)}
          />
          <small style={{ color: 'var(--gray-400)', fontSize: 11 }}>
            The bonus amount will be deducted from the user's account this many days after the deposit.
          </small>
        </div>

        <div style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            id="modal-active"
            checked={form.is_active}
            onChange={e => set('is_active', e.target.checked)}
            style={{ width: 16, height: 16 }}
          />
          <label htmlFor="modal-active" style={{ fontSize: 13, cursor: 'pointer', color: 'var(--gray-700)' }}>
            Active (users can apply this code)
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
              background: 'var(--blue-mid)', color: '#fff', fontWeight: 700,
              fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Code')}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', borderRadius: 8, border: '1.5px solid var(--gray-200)',
              background: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function UsageModal({ code, onClose }) {
  const [usages, setUsages]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getPromoCodeUsages(code.id)
      .then(res => setUsages(res.data.usages || []))
      .catch(() => toast.error('Failed to load usage history.'))
      .finally(() => setLoading(false));
  }, [code.id]);

  const th = { padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-200)' };
  const td = { padding: '8px 12px', fontSize: 13, color: 'var(--gray-700)', borderBottom: '1px solid var(--gray-100)' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div
        style={{
          background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 700,
          maxHeight: '85vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            Usage History — <span style={{ color: 'var(--blue-mid)' }}>{code.code}</span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--gray-400)' }}
          >
            x
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--gray-400)' }}>Loading...</div>
        ) : usages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--gray-400)' }}>
            No usages recorded for this code yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>User</th>
                <th style={th}>Deposit</th>
                <th style={th}>Bonus</th>
                <th style={th}>Deduct On</th>
                <th style={th}>Deducted</th>
                <th style={th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {usages.map(u => (
                <tr key={u.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{u.username}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{u.email}</div>
                  </td>
                  <td style={td}>GHS {parseFloat(u.deposit_amount).toFixed(2)}</td>
                  <td style={td}>GHS {parseFloat(u.bonus_amount).toFixed(2)}</td>
                  <td style={td}>{u.deduct_at}</td>
                  <td style={td}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: u.deducted ? '#dcfce7' : '#fef3c7',
                      color: u.deducted ? '#166534' : '#92400e',
                    }}>
                      {u.deducted ? 'Yes' : 'Pending'}
                    </span>
                  </td>
                  <td style={td}>{new Date(u.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function PromoCodes() {
  const [codes, setCodes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editTarget, setEdit]   = useState(null);   // null | false (create) | code object (edit)
  const [usageTarget, setUsage] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminAPI.promoCodes()
      .then(res => setCodes(res.data.codes || []))
      .catch(() => toast.error('Failed to load promo codes.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete promo code "${code.code}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deletePromoCode(code.id);
      toast.success('Promo code deleted.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed.');
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await adminAPI.updatePromoCode(code.id, { is_active: code.is_active ? 0 : 1 });
      toast.success(code.is_active ? 'Code deactivated.' : 'Code activated.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed.');
    }
  };

  const th = {
    padding: '10px 14px', textAlign: 'left', fontSize: 12,
    fontWeight: 700, color: 'var(--gray-500)', borderBottom: '2px solid var(--gray-200)',
    whiteSpace: 'nowrap',
  };
  const td = {
    padding: '12px 14px', fontSize: 13, color: 'var(--gray-700)',
    borderBottom: '1px solid var(--gray-100)', verticalAlign: 'middle',
  };

  return (
    <div>
      {editTarget !== null && (
        <FormModal
          initial={editTarget === false ? null : editTarget}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); load(); }}
        />
      )}
      {usageTarget && (
        <UsageModal code={usageTarget} onClose={() => setUsage(null)} />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 14, color: 'var(--gray-500)' }}>
            Create deposit bonus codes for users. Each code has its own bonus percentage, minimum deposit, expiry, and deduction schedule.
          </div>
        </div>
        <button
          onClick={() => setEdit(false)}
          style={{
            padding: '9px 18px', borderRadius: 8, border: 'none',
            background: 'var(--blue-mid)', color: '#fff', fontWeight: 700,
            fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          + Create Code
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50, color: 'var(--gray-400)' }}>Loading...</div>
      ) : codes.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60, color: 'var(--gray-400)',
          border: '2px dashed var(--gray-200)', borderRadius: 14,
        }}>
          No promo codes yet. Click "Create Code" to add one.
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr>
                  <th style={th}>Code</th>
                  <th style={th}>Bonus</th>
                  <th style={th}>Min Deposit</th>
                  <th style={th}>Deduct After</th>
                  <th style={th}>Expiry</th>
                  <th style={th}>Uses</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => {
                  const expired   = c.expiry_date && new Date(c.expiry_date) < new Date();
                  const maxedOut  = c.max_uses !== null && c.total_uses >= c.max_uses;
                  const usable    = c.is_active && !expired && !maxedOut;

                  return (
                    <tr key={c.id} style={{ opacity: c.is_active ? 1 : 0.55 }}>
                      <td style={td}>
                        <div style={{ fontWeight: 700, letterSpacing: 0.5 }}>{c.code}</div>
                        {c.label && (
                          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{c.label}</div>
                        )}
                      </td>
                      <td style={{ ...td, fontWeight: 700, color: '#16a34a' }}>
                        {parseFloat(c.bonus_percent).toFixed(0)}%
                      </td>
                      <td style={td}>
                        {parseFloat(c.min_deposit) > 0
                          ? `GHS ${parseFloat(c.min_deposit).toFixed(2)}`
                          : 'Any'}
                      </td>
                      <td style={td}>{c.deduct_after_days} days</td>
                      <td style={td}>
                        {c.expiry_date
                          ? <span style={{ color: expired ? '#dc2626' : 'inherit' }}>{c.expiry_date}</span>
                          : <span style={{ color: 'var(--gray-400)' }}>No expiry</span>
                        }
                      </td>
                      <td style={td}>
                        <span
                          style={{ cursor: 'pointer', color: 'var(--blue-mid)', textDecoration: 'underline' }}
                          onClick={() => setUsage(c)}
                        >
                          {c.total_uses}{c.max_uses !== null ? ` / ${c.max_uses}` : ''}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{
                          display: 'inline-block', padding: '3px 9px', borderRadius: 12,
                          fontSize: 11, fontWeight: 700,
                          background: usable ? '#dcfce7' : (expired || maxedOut) ? '#fef2f2' : '#f3f4f6',
                          color: usable ? '#166534' : (expired || maxedOut) ? '#991b1b' : '#374151',
                        }}>
                          {!c.is_active ? 'Inactive' : expired ? 'Expired' : maxedOut ? 'Maxed out' : 'Active'}
                        </span>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setEdit(c)}
                            style={{
                              padding: '4px 10px', borderRadius: 6, border: '1px solid var(--gray-200)',
                              background: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleActive(c)}
                            style={{
                              padding: '4px 10px', borderRadius: 6, border: '1px solid var(--gray-200)',
                              background: c.is_active ? '#fef3c7' : '#f0fdf4',
                              color: c.is_active ? '#92400e' : '#166534',
                              fontSize: 12, cursor: 'pointer', fontWeight: 600,
                            }}
                          >
                            {c.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            style={{
                              padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5',
                              background: '#fef2f2', color: '#991b1b',
                              fontSize: 12, cursor: 'pointer', fontWeight: 600,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
